import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { FractionRound, FractionLineState } from '../../../interfaces/fraction-line.interface';
import { FractionLineStageComponent } from './fraction-line-stage.component';

/**
 * Host shell for Fraction Blast. Reads `FractionLineState` from
 * `MiniGameService.session$`, drives the `<app-fraction-line-stage>`
 * (via the LAUNCH button → `stage.launch()`), and writes outcomes back
 * through `scoreFractionLineRound` + `advanceFractionLineRound`.
 *
 * Misses are silent (the child re-aims); the Hint (tick subdivisions)
 * surfaces after two honest launches, mirroring Mirror Match.
 */
@Component({
  selector: 'app-fraction-line-game',
  templateUrl: './fraction-line-game.component.html',
  styleUrls: ['./fraction-line-game.component.scss']
})
export class FractionLineGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  @ViewChild(FractionLineStageComponent) stage?: FractionLineStageComponent;

  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  attempts = 0;
  hintVisible = false;
  showSuccess = false;
  resolving = false;
  firing = false;
  lastXp = 0;

  private prevRound = -1;
  private pendingTimeouts: any[] = [];

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.resetRoundUi();
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetRoundUi(): void {
    this.attempts = 0;
    this.hintVisible = false;
    this.showSuccess = false;
    this.resolving = false;
    this.firing = false;
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  // ── Derived state ──────────────────────────────────────────────────

  get state(): FractionLineState | null {
    return (this.session?.gameState as FractionLineState) ?? null;
  }

  get round(): FractionRound | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }

  get currentRound(): number { return this.state?.currentRound ?? 0; }
  get totalRounds(): number { return this.state?.totalRounds ?? 0; }
  get roundDots(): number[] { return Array.from({ length: this.totalRounds }, (_, i) => i); }
  get solvedFlags(): boolean[] { return this.state?.solved ?? []; }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }

  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    return (this.currLang === 'ar' ? st.name.ar : st.name.en || '').split(' ')[0];
  }

  get canUseHint(): boolean {
    return !this.hintVisible && !this.resolving && this.attempts >= 2;
  }

  // ── Controls ───────────────────────────────────────────────────────

  onLaunch(): void {
    if (this.resolving || this.firing || !this.stage?.canLaunch) return;
    this.firing = true;
    this.gameAudioService.play('assets/game-mode/sound-effects/fastSoundEffect.mp3');
    this.stage.launch();
  }

  onSolved(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.gameAudioService.play('assets/game-mode/sound-effects/correctSoundEffect.mp3');
    this.miniGameService.scoreFractionLineRound(true);
    // Hold a beat longer so the partitioned-fraction reveal on the line
    // is readable before the next round slides in.
    this.showSuccess = true;
    this.later(() => this.miniGameService.advanceFractionLineRound(), 2600);
  }

  onMissed(): void {
    if (this.resolving) return;
    // Wrong launch is NOT the end — the stage shows a red picture of
    // what the child did (their own length), a soft buzz gives the
    // "wrong" feeling, and they re-aim. Answer is never revealed.
    this.firing = false;
    this.attempts++;
    this.gameAudioService.play('assets/game-mode/sound-effects/buzzerSoundEffect.mp3');
  }

  useHint(): void {
    this.hintVisible = true;
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.miniGameService.scoreFractionLineRound(false);
    this.later(() => this.miniGameService.advanceFractionLineRound(), 250);
  }
}
